import {
  addFilterParams,
  Grid,
  GridActionLink,
} from "@getregistered/greg-editors";
import { addSortParams, markClearedDefaults, nestedFilterParams } from "./resultLoader";
import { startExport } from "../../components/DownloadTray/downloadFlow";

// `defaultFilters` are the filters the grid was mounted with (GR-3701). The
// live grid state no longer carries a default the user cleared, so without
// them the download would quietly re-apply it and leave out rows the screen
// was showing.
export const downloadUrl = (baseUrl: string, grid: Grid, defaultFilters?: Grid["filters"]) => {
  const params = new URLSearchParams();
  if (grid.sort !== undefined) {
    addSortParams(params, grid.sort);
  }
  const filters = markClearedDefaults(grid.columns, defaultFilters, grid.filters);
  if (filters !== undefined) {
    addFilterParams(params, grid.columns, filters);
  }
  const query = params.toString();
  if (query === "") {
    return baseUrl;
  } else {
    return `${baseUrl}?${query}`;
  }
};

export const downloadActionLink = (
  title: string,
  baseUrl: string,
  defaultFilters?: Grid["filters"]
): GridActionLink => ({
  title,
  onClick: (grid) => (window.location.href = downloadUrl(baseUrl, grid, defaultFilters)),
});

// Extract grid params (filters, sort) for async export. Filters go through
// nestedFilterParams — the same addFilterParams serialization the results
// fetch uses — so the export filters exactly like the grid on screen. Raw
// client filter state is NOT equivalent: an emptied multi-choice selection
// stays [] in state, which the server turns into where(col: []) — `1=0` —
// and the export comes back blank while the screen shows rows (GR-3066).
export const gridParamsFromGrid = (grid: Grid): Record<string, unknown> => {
  const params: Record<string, unknown> = {};

  if (grid.sort !== undefined) {
    params.s = grid.sort.column;
    params.so = grid.sort.order;
  }

  return { ...params, ...nestedFilterParams(grid.columns, grid.filters) };
};

// Best-effort helper to close any open ActionMenu overlay (Primer closes on outside click)
const closeActionMenus = () => {
  // Blur current focus
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }

  // Try ESC on any open Primer ActionMenu buttons
  const expandedTriggers = Array.from(
    document.querySelectorAll<HTMLElement>('[aria-haspopup="true"][aria-expanded="true"]')
  );
  expandedTriggers.forEach((el) => {
    el.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "Escape",
        code: "Escape",
        bubbles: true,
      })
    );
  });

  // Fallback: dispatch an outside click on body (Primer closes on outside click)
  window.requestAnimationFrame(() => {
    const click = new MouseEvent("click", { bubbles: true });
    document.body.dispatchEvent(click);
  });
};

// Async download action link that queues an export job
const serializeParams = (params: Record<string, unknown>) => JSON.stringify(params || {});
const inFlightByKey = new Map<string, Promise<void>>();

export const asyncDownloadActionLink = (
  title: string,
  eventId: string | undefined,
  gridType: string,
  exportType: "csv" | "xlsx",
  reportId: number | undefined,
  reportExportsUrl: string,
  extraParams?: Record<string, unknown>
): GridActionLink => ({
  title,
  onClick: async (grid) => {
    const params = { ...gridParamsFromGrid(grid), ...extraParams };
    const key = `${eventId ?? "org"}-${gridType}-${exportType}-${reportId ?? "none"}-${serializeParams(params)}`;

    if (inFlightByKey.has(key)) {
      return inFlightByKey.get(key);
    }

    closeActionMenus();

    const run = (async () => {
      try {
        const result = await startExport(
          reportExportsUrl,
          gridType,
          exportType,
          params,
          reportId,
          {
            onConfirm: (exportType) => {
              // Dispatch event to show confirmation dialog
              window.dispatchEvent(
                new CustomEvent("showDownloadConfirmation", {
                  detail: { exportType },
                })
              );
            },
          }
        );

        // Start polling for completion if export started successfully
        if (result?.id) {
          const { pollExportStatus } = await import("../../components/DownloadTray/downloadFlow");
          pollExportStatus(reportExportsUrl, result.id, exportType, gridType);
        }
      } catch (error) {
        console.error("[asyncDownloadActionLink] Error starting export:", error);
        throw error;
      }
    })();

    inFlightByKey.set(key, run);

    try {
      await run;
    } finally {
      inFlightByKey.delete(key);
      closeActionMenus();
    }
  },
});
